import Joi from "joi";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import Tooltip from "rc-tooltip";
import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DateTime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import Form from "../../common/form.jsx";
import {
  Panel,
  PanelBody,
  PanelHeader,
} from "../../components/panel/panel.jsx";
import CountryDropDown from "../../components/user/CountryDropDown";
import { getLabels } from "../../services/labels";
import auth from "./../../services/authservice";
import { getUser } from "../../services/users.js";

const createSliderWithTooltip = Slider.createSliderWithTooltip;
const Handle = Slider.Handle;

class Label extends Form {
  async componentDidMount() {
    document.title = "Print Label to Zebraprinter";    
  }
  constructor(props) {
    super(props);

    var maxYesterday = "";
    var minYesterday = DateTime.moment().subtract(1, "day");

    this.minDateRange = (current) => {
      return current.isAfter(minYesterday);
    };
    this.maxDateRange = (current) => {
      return current.isAfter(maxYesterday);
    };
    this.minDateChange = (value) => {
      this.setState({
        maxDateDisabled: false,
      });
      maxYesterday = value;
    };

    const defaultPrintedOn = new Date();
    const defaultExpiredOn = new Date();
    defaultExpiredOn.setDate(defaultExpiredOn.getDate() + 30);

    this.state = {
      maxDateDisabled: true,
      madeIn: [],
      profiles: [],
      labels: [],
      loading: false,
      selectedLabel: null,
      previewUrl: null,
      isPrinting: false,
      isFormValid: false,
      showErrors: false, // Add this flag to control when to show errors
      data: {
        user: "",
        printerName: "",
        username: "",
        name: "",
        labelSize: "",
        SKU: "",
        serial: "",
        madeIn: "",
        barcodeType: "",
        expiredOn: defaultExpiredOn, // Set default expiredOn
        copies: "1",
        note: "",
        printedOn: defaultPrintedOn, // Set default printedOn
        orientation: "landscape", // Default orientation
        delivery: "",
        location: "",
        AWB: "",
        country: ""
      },
      selectedFile: null,
      errors: {},
    };

    this.handleSlider = (props) => {
      const { value, dragging, index, ...restProps } = props;
      return (
        <Tooltip
          prefixCls="rc-slider-tooltip"
          overlay={value}
          visible={dragging}
          placement="top"
          key={index}
        >
          <Handle value={value} {...restProps} />
        </Tooltip>
      );
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleLabelChange = this.handleLabelChange.bind(this);

    // Create a ref for the hidden iframe
    this.printFrame = React.createRef();
  }

  handleChange = (e) => {
    const data = { ...this.state.data };
    data[e.currentTarget.name] = e.currentTarget.value;
    this.setState({ data }, () => {
      // Only validate if errors are already being shown
      if (this.state.showErrors) {
        this.validateForm();
      }
    });
  };

  async populateLabel() {
    try {
      const { data: labels } = await getLabels();
      this.setState({ labels });
    } catch (ex) {
      console.log(ex);
      if (ex.response && ex.response.status === 404)
        this.props.history.replace("/error");
    }
  }

  async setCurrentUser() {
    const { _id: currentUser_id } = auth.getProfile();
    const { data: user } = await getUser(currentUser_id)
    this.setState({ data: { ...this.state.data, username: user.contactName?.first + " " + user.contactName?.last } });
  }

  async componentDidMount() {
    this.setCurrentUser();
    this.populateLabel();
  }

  schema = Joi.object({
    username: Joi.string().optional(),
    name: Joi.string().optional(),
    labelSize: Joi.string().optional(),
    SKU: Joi.string().when('selectedLabel.SKU', {
      is: true,
      then: Joi.string().required().label('SKU'),
      otherwise: Joi.string().optional()
    }),
    serial: Joi.string().when('selectedLabel.serial', {
      is: true,
      then: Joi.string().required().label('Serial'),
      otherwise: Joi.string().optional()
    }),
    madeIn: Joi.string().when('selectedLabel.madeIn', {
      is: Joi.array().min(1),
      then: Joi.string().required().label('Made In'),
      otherwise: Joi.string().optional()
    }),
    copies: Joi.string().required().label('Copies'),
    printerName: Joi.string().optional(),
    printedOn: Joi.bool().optional(),
    orientation: Joi.string().valid('portrait', 'landscape').optional(),
    expiredOn: Joi.date().when('selectedLabel.expiredOn', {
      is: true,
      then: Joi.date().required().label('Expired Date'),
      otherwise: Joi.date().optional()
    }),
    barcodeType: Joi.string().optional(),
    note: Joi.string().optional(),
    delivery: Joi.string().when('selectedLabel.delivery', {
      is: true,
      then: Joi.string().required().label('Delivery'),
      otherwise: Joi.string().optional()
    }),
    location: Joi.string().when('selectedLabel.location', {
      is: true,
      then: Joi.string().required().label('Location'),
      otherwise: Joi.string().optional()
    }),
    AWB: Joi.string().when('selectedLabel.AWB', {
      is: true,
      then: Joi.string().required().label('AWB'),
      otherwise: Joi.string().optional()
    }),
    country: Joi.string().when('selectedLabel.country', {
      is: true,
      then: Joi.string().required().label('Country'),
      otherwise: Joi.string().optional()
    }),
  });

  validateForm = () => {
    const { data, selectedLabel } = this.state;
    const errors = {};
    let isValid = true;

    // Check if label is selected
    if (!selectedLabel) {
      isValid = false;
      errors.label = "Label is required";
    }

    // Check required fields based on selected label configuration
    if (selectedLabel) {
      if (selectedLabel.SKU && !data.SKU) {
        errors.SKU = "SKU is required";
        isValid = false;
      }

      if (selectedLabel.serial && !data.serial) {
        errors.serial = "Serial is required";
        isValid = false;
      }

      if (selectedLabel.madeIn && selectedLabel.madeIn[0] && !data.madeIn) {
        errors.madeIn = "Made In is required";
        isValid = false;
      }

      if (selectedLabel.expiredOn && !data.expiredOn) {
        errors.expiredOn = "Expired Date is required";
        isValid = false;
      }

      if (selectedLabel.country && !data.country) {
        errors.country = "Country is required";
        isValid = false;
      }

      if (selectedLabel.delivery && !data.delivery) {
        errors.delivery = "Delivery is required";
        isValid = false;
      }

      if (selectedLabel.location && !data.location) {
        errors.location = "Location is required";
        isValid = false;
      }

      if (selectedLabel.AWB && !data.AWB) {
        errors.AWB = "AWB is required";
        isValid = false;
      }

      if (!data.copies) {
        errors.copies = "Copies is required";
        isValid = false;
      }
    }

    this.setState({ errors, isFormValid: isValid });
    return isValid;
  };


  handleLabelChange = (e) => {
    const labelId = e.target.value;
    const selectedLabel = this.state.labels.find(label => label._id === labelId);

    this.setState({
      selectedLabel,
      data: {
        ...this.state.data,
        label: labelId
      }
    }, () => {
      // Only validate if errors are already being shown
      if (this.state.showErrors) {
        this.validateForm();
      }
    });
  };

  handledateChange = (date, field) => {
    const data = { ...this.state.data };
    data[field] = date;
    this.setState({ data }, () => {
      if (this.state.showErrors) {
        this.validateForm();
      }
    });
  };

  // Key modifications to integrate label settings from selectedLabel

  generateZPL = (labelData) => {
    const {
      country = 'United States (USA)',
      madeIn = 'United States (USA)',
      SKU = '123456',
      username = "Shahama Mahb",
      barcode = '12345678',
      AWB = 'Ctr. X34B – 1',
      delivery = 'REF1 FO0B47',
      location = 'REF2 BL4H8',
      printedOn = new Date(),
      expiredOn = new Date(),
      note = '',
      orientation = 'portrait'
    } = labelData;

    // Get settings from selectedLabel
    const fontSize = this.state.selectedLabel?.fontSize || '25';
    const barcodeType = this.state.selectedLabel?.barcodeType || 'Code128';
    const labelSize = this.state.selectedLabel?.value || '101.6x152.4';

    // Parse label dimensions from labelSize
    const [widthStr, heightStr] = labelSize.split('x');
    const width = this.parseDimension(widthStr);
    const height = this.parseDimension(heightStr);

    // Convert font size to ZPL font size
    const zplFontSize = Math.max(20, Math.min(50, parseInt(fontSize) * 2));

    // Format dates for display with time
    const formatDateTime = (date) => {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
    };

    // Adjust coordinates based on orientation
    const isLandscape = orientation === 'landscape';
    const labelWidth = isLandscape ? height : width;
    const labelHeight = isLandscape ? width : height;

    // Start ZPL
    let zpl = `^XA`;
    let yPosition = 20;

    // Add fields only if they are enabled in selectedLabel
    if (this.state.selectedLabel.printUsername) {
      zpl += `\n^FO20,${yPosition}^A0N,${Math.round(zplFontSize * 0.8)},${Math.round(zplFontSize * 0.8)}^FD${username}^FS`;
      yPosition += zplFontSize + 10;
    }

    if (this.state.selectedLabel.country) {
      zpl += `\n^FO20,${yPosition}^A0N,${Math.round(zplFontSize * 0.8)},${Math.round(zplFontSize * 0.8)}^FD${country}^FS`;
      yPosition += zplFontSize + 10;
    }

    if (this.state.selectedLabel.delivery) {
      zpl += `\n^FO20,${yPosition}^A0N,${Math.round(zplFontSize * 0.8)},${Math.round(zplFontSize * 0.8)}^FD${delivery}^FS`;
      yPosition += zplFontSize + 10;
    }

    if (this.state.selectedLabel.location) {
      zpl += `\n^FO20,${yPosition}^A0N,${Math.round(zplFontSize * 0.8)},${Math.round(zplFontSize * 0.8)}^FD${location}^FS`;
      yPosition += zplFontSize + 10;
    }

    if (this.state.selectedLabel.madeIn && this.state.selectedLabel.madeIn[0]) {
      zpl += `\n^FO20,${yPosition}^A0N,${Math.round(zplFontSize * 0.8)},${Math.round(zplFontSize * 0.8)}^FDMade In ${madeIn}^FS`;
      yPosition += zplFontSize + 10;
    }

    zpl += `\n^FO20,${yPosition}^GB${labelWidth - 40},3,3^FS`;
    yPosition += 20;

    if (this.state.selectedLabel.SKU) {
      // Add SKU as text
      zpl += `\n^FO20,${yPosition}^A0N,${Math.round(zplFontSize * 0.8)},${Math.round(zplFontSize * 0.8)}^FDSKU: ${SKU}^FS`;
      yPosition += zplFontSize + 10;

      // Add SKU as barcode
      const barcodeCommand = this.getBarcodeCommand(barcodeType);
      zpl += `\n^FO20,${yPosition}^${barcodeCommand}^FD${SKU}^FS`;
      yPosition += 120;
    }

    zpl += `\n^FO20,${yPosition}^GB${labelWidth - 40},3,3^FS`;
    yPosition += 20;

    if (this.state.selectedLabel.serial) {
      // Use the selected barcode type
      const barcodeCommand = this.getBarcodeCommand(barcodeType);
      zpl += `\n^FO20,${yPosition}^${barcodeCommand}^FD${barcode}^FS`;
      yPosition += 120;
    }

    zpl += `\n^FO20,${yPosition}^GB${labelWidth - 40},3,3^FS`;
    yPosition += 20;

    if (this.state.selectedLabel.printedOn) {
      zpl += `\n^FO20,${yPosition}^A0N,${Math.round(zplFontSize * 0.8)},${Math.round(zplFontSize * 0.8)}^FDPrinted: ${formatDateTime(printedOn)}^FS`;
      yPosition += zplFontSize + 10;
    }

    if (this.state.selectedLabel.expiredOn) {
      zpl += `\n^FO20,${yPosition}^A0N,${Math.round(zplFontSize * 0.8)},${Math.round(zplFontSize * 0.8)}^FDExpires: ${formatDateTime(expiredOn)}^FS`;
      yPosition += zplFontSize + 10;
    }

    if (this.state.selectedLabel.note && note) {
      zpl += `\n^FO20,${yPosition}^A0N,${Math.round(zplFontSize * 0.6)},${Math.round(zplFontSize * 0.6)}^FDNote: ${note}^FS`;
      yPosition += zplFontSize + 10;
    }

    if (this.state.selectedLabel.AWB) {
      zpl += `\n^FO20,${yPosition}^A0N,${Math.round(zplFontSize * 0.8)},${Math.round(zplFontSize * 0.8)}^FD${AWB}^FS`;
      yPosition += zplFontSize + 10;
    }

    zpl += `\n^XZ`;

    return zpl;
  };

  // 2. Add helper function to parse label dimensions
  parseDimension = (dimStr) => {
    // Convert dimensions from the object (e.g., "101.6") to pixels (assuming 203 DPI)
    const dpi = 203;
    const mm = parseFloat(dimStr);
    return Math.round((mm / 25.4) * dpi); // Convert mm to inches then to pixels
  };

  // 3. Add helper function to get barcode command based on type
  getBarcodeCommand = (barcodeType) => {
    const barcodeMap = {
      'UPC-E': 'B9N,N,100,Y,N',
      'UPC-A': 'BUN,100,Y,N,Y',
      'JAN/EAN8': 'B8N,N,100,Y,N',
      'JAN/EAN13': 'BEN,N,100,Y,N',
      'Code39': 'B3N,N,100,Y,N',
      'ITF': 'BIN,N,100,Y,N',
      'Code128': 'BCN,100,Y,N,N',
      'Code93': 'BAN,N,100,Y,N',
      'NW-7': 'BNN,N,100,Y,N'
    };

    return barcodeMap[barcodeType] || 'BCN,100,Y,N,N'; // Default to Code128
  };

  // 4. Update getLabelPreview to use selectedLabel settings
  getLabelPreview = async (zplCode) => {
    try {
      this.setState({ loading: true });

      const labelSize = this.state.selectedLabel?.value || '101.6x152.4';
      const printQuality = this.state.selectedLabel?.printQuality || '8dpmm';
      const orientation = this.state.data.orientation || 'portrait';

      // Parse dimensions from labelSize (in mm)
      const [widthStr, heightStr] = labelSize.split('x');
      let width = parseFloat(widthStr);
      let height = parseFloat(heightStr);

      // Convert mm to inches for Labelary API
      width = width / 25.4;
      height = height / 25.4;

      // Swap dimensions if landscape
      if (orientation === 'landscape') {
        [width, height] = [height, width];
      }

      // Extract DPI from printQuality
      let dpmm = '8'; // Default
      if (printQuality.includes('dpmm')) {
        dpmm = printQuality.replace('dpmm', '');
      } else if (printQuality.includes('dpi')) {
        const dpi = printQuality.replace('dpi', '');
        dpmm = Math.round(parseInt(dpi) / 25.4).toString();
      }

      const response = await fetch(
        `http://api.labelary.com/v1/printers/${dpmm}dpmm/labels/${width}x${height}/0/`,
        {
          method: 'POST',
          headers: {
            'Accept': 'image/png',
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: zplCode
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        this.setState({ previewUrl: imageUrl, loading: false });
        return imageUrl;
      } else {
        throw new Error('Failed to generate label preview');
      }
    } catch (error) {
      console.error('Error generating label preview:', error);
      this.setState({ loading: false });
      return null;
    }
  };

  // 5. Update printLabelDirectly to use dynamic label dimensions
  printLabelDirectly = async (zplCode, copies = 1) => {
    try {
      this.setState({ isPrinting: true });

      const previewUrl = await this.getLabelPreview(zplCode);

      if (!previewUrl) {
        throw new Error('Failed to generate label preview');
      }

      const printIframe = this.printFrame.current;
      if (!printIframe) {
        throw new Error('Print iframe not found');
      }

      const iframeDoc = printIframe.contentDocument || printIframe.contentWindow.document;

      // Get label dimensions from selectedLabel
      const labelSize = this.state.selectedLabel?.value || '101.6x152.4';
      const [widthStr, heightStr] = labelSize.split('x');
      let widthMM = parseFloat(widthStr);
      let heightMM = parseFloat(heightStr);

      // Convert mm to inches for CSS
      const widthInches = widthMM / 25.4;
      const heightInches = heightMM / 25.4;

      // Swap if landscape
      const orientation = this.state.data.orientation || 'portrait';
      const finalWidth = orientation === 'landscape' ? heightInches : widthInches;
      const finalHeight = orientation === 'landscape' ? widthInches : heightInches;

      iframeDoc.open();
      iframeDoc.write(`
      <html>
        <head>
          <title>Print Label</title>
          <style>
            body { 
              margin: 0 !important;
              padding: 0 !important;
              display: flex;
              justify-content: center;
              align-items: center;
              width: ${finalWidth}in;
              height: ${finalHeight}in;
              overflow: hidden;
            }
            .print-container {
              width: ${finalWidth}in;
              height: ${finalHeight}in;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            img { 
              width: ${finalWidth}in;
              height: ${finalHeight}in;
              object-fit: contain;
            }
            @media print {
              body, html {
                width: ${finalWidth}in !important;
                height: ${finalHeight}in !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              .print-container {
                width: 100% !important;
                height: 100% !important;
                page-break-inside: avoid;
              }
              img {
                width: 100% !important;
                height: 100% !important;
              }
              @page {
                size: ${finalWidth}in ${finalHeight}in;
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <img src="${previewUrl}" alt="Label" onload="window.focus(); window.print();" />
          </div>
          <script>
            // Set print settings to ensure proper scaling
            function setPrintSettings() {
              return {
                scale: 1,
                shrinkToFit: false,
                printBackground: true,
                preferCSSPageSize: true
              };
            }
          </script>
        </body>
      </html>
    `);
      iframeDoc.close();

    } catch (error) {
      console.error('Error printing label:', error);
      this.setState({ isPrinting: false });
    }
  };

  doSubmit = async () => {
    this.setState({ showErrors: true }, () => {
      if (!this.validateForm()) {
        return;
      }

      const { data, selectedLabel } = this.state;

      const labelData = {
        companyName: `${selectedLabel?.user?.contactName?.first} ${selectedLabel?.user?.contactName?.last}` || 'Your Company Name',
        companyAddress: `${selectedLabel?.user?.Address?.address1} ${selectedLabel?.user?.Address?.city} ${selectedLabel?.user?.Address?.state} ${selectedLabel?.user?.Address?.country}` || 'Your Company Address',
        companyCityStateZip: selectedLabel?.user?.Address?.zip || 'City, State ZIP',
        country: data.country || 'Country',
        recipientName: 'Recipient Name',
        recipientAddress: 'Recipient Address',
        recipientCityStateZip: 'Recipient City, State ZIP',
        madeIn: data.madeIn || 'Country of Origin',
        SKU: data.SKU || 'N/A',
        username: data.username || 'N/A',
        barcode: data.serial || '12345678',
        AWB: data.AWB || 'Container Number',
        delivery: data.delivery || 'Delivery Info',
        location: data.location || 'Location',
        expiredOn: data.expiredOn || new Date(),
        printedOn: data.printedOn || new Date(),
        note: data.note || '',
        orientation: data.orientation || 'portrait'
      };

      const zpl = this.generateZPL(labelData);
      this.printLabelDirectly(zpl, parseInt(data.copies || 1));
    });
  };

  handlePreview = async () => {
    this.setState({ showErrors: true }, async () => {
      if (!this.validateForm()) {
        return;
      }

      const { data, selectedLabel } = this.state;

      const labelData = {
        companyName: `${selectedLabel?.user?.contactName?.first} ${selectedLabel?.user?.contactName?.last}` || 'Your Company Name',
        companyAddress: `${selectedLabel?.user?.Address?.address1} ${selectedLabel?.user?.Address?.city} ${selectedLabel?.user?.Address?.state} ${selectedLabel?.user?.Address?.country}` || 'Your Company Address',
        companyCityStateZip: selectedLabel?.user?.Address?.zip || 'City, State ZIP',
        country: data.country || 'Country',
        recipientName: 'Recipient Name',
        recipientAddress: 'Recipient Address',
        recipientCityStateZip: 'Recipient City, State ZIP',
        madeIn: data.madeIn || 'Country of Origin',
        SKU: data.SKU || 'N/A',
        username: data.username || 'N/A',
        barcode: data.serial || '12345678',
        AWB: data.AWB || 'Container Number',
        delivery: data.delivery || 'Delivery Info',
        location: data.location || 'Location',
        expiredOn: data.expiredOn || new Date(),
        printedOn: data.printedOn || new Date(),
        note: data.note || '',
        orientation: data.orientation || 'portrait'
      };

      const zpl = this.generateZPL(labelData);
      await this.getLabelPreview(zpl);
    });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.doSubmit();
  };

  render() {
    const { data, errors, selectedLabel, previewUrl, loading, isPrinting, isFormValid, showErrors } = this.state;

    return (
      <React.Fragment>
        <div>
          <h1 className="page-header">
            Add Label <small>Label-registration-form</small>
          </h1>

          <div className="row">
            <div className="col-xl-10">
              <Panel>
                <PanelHeader>Add Label</PanelHeader>
                <PanelBody className="panel-form">
                  <form
                    className="form-horizontal form-bordered"
                    onSubmit={this.handleSubmit}
                  >
                    <div className="form-group row">
                      <label className="col-lg-4 col-form-label">Label *</label>
                      <div className="col-lg-8">
                        <select
                          name="label"
                          id="label"
                          value={data.label || ""}
                          onChange={this.handleLabelChange}
                          className="form-control"
                          required
                        >
                          <option value="">Select Label</option>
                          {this.state.labels.map((label) => (
                            <option key={label._id} value={label._id}>
                              {label.name}
                            </option>
                          ))}
                        </select>
                        {showErrors && !selectedLabel && errors.label && (
                          <div className="text-danger small mt-1">
                            {errors.label}
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedLabel && (
                      <>
                        {selectedLabel.SKU &&
                          this.renderInput("SKU", "SKU *", "text", "Enter SKU", "", showErrors && errors.SKU)}

                        {selectedLabel.serial &&
                          this.renderInput("serial", "Serial *", "text", "Enter serial", "", showErrors && errors.serial)}

                        {selectedLabel.madeIn && selectedLabel.madeIn[0] && (
                          <div className="form-group row">
                            <label className="col-lg-4 col-form-label" htmlFor="madeIn">
                              Made In *
                            </label>
                            <div className="col-lg-8">
                              <CountryDropDown
                                changeHandler={(e) => {
                                  const data = { ...this.state.data };
                                  data.madeIn = e;
                                  this.setState({ data }, () => {
                                    if (this.state.showErrors) {
                                      this.validateForm();
                                    }
                                  });
                                }}
                                selectedValue={
                                  data.madeIn ? data.madeIn : "MadeIn"
                                }
                                name="madeIn"
                              />
                              {showErrors && errors.madeIn && (
                                <div className="text-danger small mt-1">
                                  {errors.madeIn}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {selectedLabel.expiredOn && (
                          <div className="form-group row">
                            <label className="col-lg-4 col-form-label" htmlFor="expiredOn">
                              Expired Date *
                            </label>
                            <div className="col-lg-8">
                              <DatePicker
                                onChange={(e) => this.handledateChange(e, "expiredOn")}
                                id="expiredOn"
                                value={data.expiredOn}
                                selected={data.expiredOn}
                                inputProps={{ placeholder: "Datepicker" }}
                                className="form-control"
                              />
                              {showErrors && errors.expiredOn && (
                                <div className="text-danger small mt-1">
                                  {errors.expiredOn}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {selectedLabel.country && (
                          <div className="form-group row">
                            <label className="col-lg-4 col-form-label" htmlFor="country">
                              Country *
                            </label>
                            <div className="col-lg-8">
                              <CountryDropDown
                                changeHandler={(e) => {
                                  const data = { ...this.state.data };
                                  data.country = e;
                                  this.setState({ data }, () => {
                                    if (this.state.showErrors) {
                                      this.validateForm();
                                    }
                                  });
                                }}
                                selectedValue={
                                  data.country ? data.country : "Country"
                                }
                                name="country"
                              />
                              {showErrors && errors.country && (
                                <div className="text-danger small mt-1">
                                  {errors.country}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {selectedLabel.printUsername &&
                          this.renderInput("username", "User Name", "text", "Enter User Name")}

                        {selectedLabel.note && (
                          <div className="form-group row">
                            <label className="col-lg-4 col-form-label">Note</label>
                            <div className="col-lg-8">
                              <div className="row row-space-5">
                                <textarea
                                  name="note"
                                  id="note"
                                  cols="30"
                                  rows="5"
                                  className="form-control m-b-5"
                                  placeholder="Enter Note"
                                  value={data.note}
                                  onChange={this.handleChange}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Orientation Field */}
                        {selectedLabel.orientation && (
                          <div className="form-group row">
                            <label className="col-lg-4 col-form-label">Orientation</label>
                            <div className="col-lg-8">
                              <select
                                name="orientation"
                                id="orientation"
                                value={data.orientation || "portrait"}
                                onChange={this.handleChange}
                                className="form-control"
                              >
                                <option value="portrait">Portrait</option>
                                <option value="landscape">Landscape</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {selectedLabel.delivery &&
                          this.renderInput("delivery", "Delivery *", "text", "Enter delivery info", "", showErrors && errors.delivery)}

                        {selectedLabel.location &&
                          this.renderInput("location", "Location *", "text", "Enter location", "", showErrors && errors.location)}

                        {selectedLabel.AWB &&
                          this.renderInput("AWB", "AWB *", "text", "Enter AWB", "", showErrors && errors.AWB)}

                        {selectedLabel.printedOn &&
                          <div className="form-group row">
                            <label className="col-lg-4 col-form-label" htmlFor="printedOn">
                              Printed On
                            </label>
                            <div className="col-lg-8">
                              <DatePicker
                                onChange={(e) => this.handledateChange(e, "printedOn")}
                                id="printedOn"
                                selected={data.printedOn}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="MMMM d, yyyy h:mm aa"
                                className="form-control"
                              />
                            </div>
                          </div>
                        }

                        {this.renderInput("copies", "Copies *", "number", "Enter copies", "1", showErrors && errors.copies)}

                        {/* Preview and Print Buttons */}
                        <div className="form-group row">
                          <div className="col-lg-8 col-lg-offset-4">
                            <button
                              type="button"
                              onClick={this.handlePreview}
                              disabled={loading}
                              className="btn btn-info btn-sm m-r-5"
                            >
                              {loading ? 'Generating Preview...' : 'Preview Label'}
                            </button>
                            <button
                              type="submit"
                              // disabled={isPrinting}
                              className="btn btn-primary btn-sm m-r-5"
                            >
                              {/* {isPrinting ? 'Printing...' : 'Print Label'} */}
                              Print Label
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>

                        {/* Validation summary */}
                        {showErrors && !isFormValid && (
                          <div className="form-group row">
                            <div className="col-lg-8 col-lg-offset-4">
                              <div className="alert alert-danger">
                                Please fill in all required fields (marked with *)
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Preview Image */}
                        {previewUrl && (
                          <div className="form-group row">
                            <div className="col-lg-8 col-lg-offset-4">
                              <div className="panel panel-default">
                                <div className="panel-heading">Label Preview</div>
                                <div className="panel-body">
                                  <img
                                    src={previewUrl}
                                    alt="Label Preview"
                                    style={{ maxWidth: '100%', border: '1px solid #ddd' }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </form>
                </PanelBody>
              </Panel>
            </div>
          </div>
        </div>

        {/* Hidden iframe for printing */}
        <iframe
          ref={this.printFrame}
          title="printFrame"
          style={{ display: 'none' }}
        />
      </React.Fragment>
    );
  }
}

export default Label;
